<script setup lang="ts">
import { ref, computed, defineAsyncComponent } from 'vue'
import { useHead } from '@unhead/vue'
import Card from 'primevue/card'

const Carousel = defineAsyncComponent(() => import('primevue/carousel'))
import iosImage from '../assets/ios-app.webp'
import androidImage from '../assets/android-app.webp'
import crossPlatformImage from '../assets/cross-app.webp'
import webImage from '../assets/web-development.webp'
import customSoftwareImage from '../assets/software-development.webp'
import uiuxImage from '../assets/ui-ux-design.webp'
import heroBgImage from '../assets/hero-bg.webp'
import { useProjectsStore } from '../stores/projects'
import { useBlogStore } from '../stores/blog'
import { useLanguageStore } from '@/stores/language'

const languageStore = useLanguageStore()
const projectsStore = useProjectsStore()
const blogStore = useBlogStore()
const projects = computed(() => projectsStore.getAllProjects)
const blogPosts = ref(blogStore.getAllPosts)

const homeContent = computed(() => languageStore.home)
const hero = computed(() => homeContent.value.hero)
const stats = computed(() => homeContent.value.stats)
const techBadges = computed(() => homeContent.value.techBadges)
const sections = computed(() => homeContent.value.sections)
const labels = computed(() => homeContent.value.labels)
const services = computed(() => homeContent.value.services)
const meta = computed(() => homeContent.value.meta)

const organizationSchema = computed(() => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Efes Tech',
  alternateName: 'Efes Tech',
  url: 'https://efestech.com',
  logo: 'https://efestech.com/assets/logo.png',
  description: meta.value.description,
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'EVLİYA ÇELEBİ MAH. SADİ KONURALP CAD. IKSV VAKFI NO: 5 İÇ KAPI NO: 2',
    addressLocality: 'Beyoğlu',
    addressRegion: 'İstanbul',
    addressCountry: 'TR',
    postalCode: '34433',
  },
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+90-536-788-44-50',
    contactType: 'Customer Service',
    email: 'admin@efestech.com',
    availableLanguage: ['Turkish', 'English'],
  },
  sameAs: [
    'https://www.linkedin.com/company/efes-bilgi-teknolojileri',
    'https://www.instagram.com/efestech',
    'https://youtube.com/@efestech',
    'https://www.tiktok.com/@efestechcom',
  ],
  areaServed: 'Worldwide',
  knowsAbout: [
    'Mobile App Development',
    'iOS Development',
    'Android Development',
    'Web Development',
    'Software Development',
    'UI/UX Design',
  ],
  foundingDate: '2020',
  numberOfEmployees: {
    '@type': 'QuantitativeValue',
    minValue: 5,
    maxValue: 10,
  },
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.8',
    ratingCount: '50',
    bestRating: '5',
    worstRating: '1',
  },
  founder: {
    '@type': 'Person',
    name: 'Efes Tech Team',
  },
  employee: [
    {
      '@type': 'Person',
      jobTitle: 'Software Developer',
      knowsAbout: ['iOS Development', 'Swift', 'SwiftUI'],
    },
    {
      '@type': 'Person',
      jobTitle: 'Software Developer',
      knowsAbout: ['Android Development', 'Kotlin', 'Jetpack Compose'],
    },
    {
      '@type': 'Person',
      jobTitle: 'Software Developer',
      knowsAbout: ['Web Development', 'Vue.js', 'React', 'TypeScript'],
    },
  ],
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Software Development Services',
    itemListElement: [
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'iOS App Development',
          description: 'Native iOS application development using Swift and SwiftUI',
          provider: {
            '@type': 'Organization',
            name: 'Efes Tech',
          },
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Android App Development',
          description: 'Native Android application development using Kotlin and Jetpack Compose',
          provider: {
            '@type': 'Organization',
            name: 'Efes Tech',
          },
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Web Development',
          description: 'Modern web application development using Vue.js, React, and TypeScript',
          provider: {
            '@type': 'Organization',
            name: 'Efes Tech',
          },
        },
      },
    ],
  },
}))

const projectsListSchema = computed(() => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: sections.value.projectsTitle,
  description: sections.value.projectsSubtitle,
  itemListElement: projects.value.map((project, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    item: {
      '@type': 'SoftwareApplication',
      name: project.title,
      description: project.description,
      url: `https://efestech.com${project.projectLink}`,
      image: `https://efestech.com${project.image}`,
      applicationCategory: 'ProductivityApplication',
      operatingSystem:
        project.technologies.includes('iOS') || project.technologies.includes('SwiftUI')
          ? 'iOS'
          : project.technologies.includes('Android') || project.technologies.includes('Kotlin')
            ? 'Android'
            : 'Cross-Platform',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
      },
      aggregateRating: project.development
        ? undefined
        : {
            '@type': 'AggregateRating',
            ratingValue: '4.5',
            ratingCount: '100',
          },
      publisher: {
        '@type': 'Organization',
        name: 'Efes Tech',
        url: 'https://efestech.com',
      },
      ...(() => {
        const downloadUrls: string[] = []
        if (project.appStoreLink && project.appStoreLink !== '#') {
          downloadUrls.push(project.appStoreLink)
        }
        if (project.playStoreLink && project.playStoreLink !== '#') {
          downloadUrls.push(project.playStoreLink)
        }
        return downloadUrls.length > 0
          ? { downloadUrl: downloadUrls.length === 1 ? downloadUrls[0] : downloadUrls }
          : {}
      })(),
    },
  })),
}))

useHead(() => {
  const isEn = languageStore.currentLanguage === 'en'
  const currentUrl = isEn ? 'https://efestech.com/en' : 'https://efestech.com'

  return {
    title: meta.value.title,
    meta: [
      {
        name: 'description',
        content: meta.value.description,
      },
      {
        name: 'keywords',
        content: meta.value.keywords,
      },
      {
        property: 'og:title',
        content: meta.value.title,
      },
      {
        property: 'og:description',
        content: meta.value.description,
      },
      {
        property: 'og:type',
        content: 'website',
      },
      {
        property: 'og:url',
        content: currentUrl,
      },
      {
        property: 'og:image',
        content: 'https://efestech.com/assets/logo.png',
      },
      {
        property: 'og:image:alt',
        content: 'Efes Tech Logo',
      },
      {
        property: 'og:locale',
        content: isEn ? 'en_US' : 'tr_TR',
      },
      {
        property: 'og:locale:alternate',
        content: isEn ? 'tr_TR' : 'en_US',
      },
      {
        name: 'twitter:card',
        content: 'summary_large_image',
      },
      {
        name: 'twitter:title',
        content: meta.value.title,
      },
      {
        name: 'twitter:description',
        content: meta.value.description,
      },
      {
        name: 'twitter:image',
        content: 'https://efestech.com/assets/logo.png',
      },
      {
        name: 'robots',
        content: 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
      },
      {
        name: 'googlebot',
        content: 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
      },
    ],
    link: [
      {
        rel: 'canonical',
        href: currentUrl,
      },
      {
        rel: 'alternate',
        hreflang: 'tr',
        href: 'https://efestech.com',
      },
      {
        rel: 'alternate',
        hreflang: 'en',
        href: 'https://efestech.com/en',
      },
      {
        rel: 'alternate',
        hreflang: 'x-default',
        href: 'https://efestech.com',
      },
      {
        rel: 'preload',
        as: 'image',
        href: heroBgImage,
        fetchpriority: 'high',
      },
    ],
    script: [
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify(organizationSchema.value),
      },
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify(projectsListSchema.value),
      },
    ],
  }
})

const serviceImages = [
  iosImage,
  androidImage,
  crossPlatformImage,
  webImage,
  customSoftwareImage,
  uiuxImage,
]

const servicesWithImages = computed(() =>
  services.value.map((service: any, index: number) => ({
    ...service,
    image: serviceImages[index] ?? serviceImages[serviceImages.length - 1],
  })),
)
const getServiceLink = (service: any, index: number) => {
  if (
    index === 4 ||
    service.title?.toLowerCase().includes('özel yazılım') ||
    service.title?.toLowerCase().includes('custom software')
  ) {
    return languageStore.localizePath('/ozel-yazilim-gelistirme')
  }
  return languageStore.localizePath('/quote')
}
</script>

<template>
  <main class="home-container">
    <section class="hero-section">
      <div class="hero-content">
        <h1 class="main-title">
          <span class="highlight">{{ hero.highlight }}</span
          ><br />
          {{ hero.title }}
        </h1>
        <p class="subtitle">{{ hero.subtitle }}</p>
        <div class="hero-stats">
          <div class="stat-item" v-for="stat in stats" :key="stat.label">
            <span class="stat-number">{{ stat.number }}</span>
            <span class="stat-label">{{ stat.label }}</span>
          </div>
        </div>
        <div class="tech-badges">
          <span class="tech-badge" v-for="badge in techBadges" :key="badge">{{ badge }}</span>
        </div>
      </div>
    </section>

    <section class="services-section">
      <div class="section-header">
        <h2 class="section-title">{{ sections.servicesTitle }}</h2>
        <p class="section-subtitle">{{ sections.servicesSubtitle }}</p>
      </div>
      <div class="cards-container">
        <Card v-for="(service, index) in servicesWithImages" :key="service.title" class="service-card">
          <template #header>
            <div class="card-image">
              <img
                :src="service.image"
                :alt="service.title + ' - Efes Tech'"
                loading="lazy"
                width="300"
                height="200"
              />
              <div class="image-overlay">
                <i :class="service.icon"></i>
              </div>
            </div>
          </template>
          <template #title>
            <div class="service-title">
              <h3>{{ service.title }}</h3>
            </div>
          </template>
          <template #content>
            <p class="service-description">{{ service.description }}</p>
            <router-link
              :to="getServiceLink(service, index)"
              class="learn-more-btn"
              :aria-label="`${labels.learnMore} - ${service.title}`"
            >
              <span>{{ labels.learnMore }}</span>
              <i class="pi pi-arrow-right" aria-hidden="true"></i>
            </router-link>
          </template>
        </Card>
      </div>
    </section>

    <section class="projects-section">
      <div class="section-header">
        <h2 class="section-title">{{ sections.projectsTitle }}</h2>
        <p class="section-subtitle">{{ sections.projectsSubtitle }}</p>
      </div>
      <Carousel
        :value="projects"
        :numVisible="5"
        :numScroll="1"
        :circular="true"
        :showNavigators="false"
        :touchable="true"
        :responsiveOptions="[
          {
            breakpoint: '1400px',
            numVisible: 4,
            numScroll: 1,
          },
          {
            breakpoint: '1200px',
            numVisible: 3,
            numScroll: 1,
          },
          {
            breakpoint: '992px',
            numVisible: 2,
            numScroll: 1,
          },
          {
            breakpoint: '768px',
            numVisible: 1,
            numScroll: 1,
          },
        ]"
        class="projects-carousel"
      >
        <template #item="slotProps">
          <div class="carousel-item">
            <router-link
              :to="languageStore.localizePath(`/projects/${slotProps.data.id}`)"
              class="project-link"
              :aria-label="slotProps.data.title"
            >
              <Card class="project-card">
                <template #content>
                  <div class="project-logo">
                    <img :src="slotProps.data.image" :alt="slotProps.data.title" loading="lazy" width="80" height="80" />
                  </div>
                  <div class="project-info">
                    <h3 class="project-title">{{ slotProps.data.title }}</h3>
                    <p class="project-description">{{ slotProps.data.description }}</p>
                  </div>
                </template>
              </Card>
            </router-link>
          </div>
        </template>
      </Carousel>
    </section>

    <section class="blog-section">
      <div class="section-header">
        <h2 class="section-title">{{ sections.blogTitle }}</h2>
        <p class="section-subtitle">{{ sections.blogSubtitle }}</p>
      </div>
      <Carousel
        :value="blogPosts"
        :numVisible="3"
        :numScroll="1"
        :circular="true"
        :showNavigators="false"
        :touchable="true"
        :responsiveOptions="[
          {
            breakpoint: '1200px',
            numVisible: 2,
            numScroll: 1,
          },
          {
            breakpoint: '768px',
            numVisible: 1,
            numScroll: 1,
          },
        ]"
        class="blog-carousel"
      >
        <template #item="slotProps">
          <div class="carousel-item">
            <router-link
              :to="languageStore.localizePath(`/blog/${slotProps.data.slug}`)"
              class="blog-link"
              :aria-label="`${slotProps.data.title} - ${labels.readMore}`"
            >
              <Card class="blog-card">
                <template #header>
                  <div class="blog-image">
                    <img :src="slotProps.data.image" :alt="slotProps.data.title" loading="lazy" width="400" height="250" />
                  </div>
                </template>
                <template #content>
                  <div class="blog-content">
                    <div class="blog-meta">
                      <span class="blog-date">{{ slotProps.data.date }}</span>
                      <span class="blog-read-time">{{ slotProps.data.readTime }}</span>
                    </div>
                    <h3 class="blog-title">{{ slotProps.data.title }}</h3>
                    <p class="blog-summary">{{ slotProps.data.summary }}</p>
                    <span class="read-more"
                      >{{ labels.readMore }} <i class="pi pi-arrow-right"></i
                    ></span>
                  </div>
                </template>
              </Card>
            </router-link>
          </div>
        </template>
      </Carousel>
    </section>
  </main>
</template>

<style scoped>
.home-container {
  max-width: 100%;
  margin: -100px 0 0 0; /* Pull up to hide the white space left by navbar */
  padding: 0;
  overflow-x: hidden;
  position: relative;
}

.hero-section {
  text-align: center;
  margin: 0;
  padding: 10rem 2rem 8rem;
  background-color: #0f172a;
  background-image: url('@/assets/hero-bg.webp');
  background-size: cover;
  background-position: center;
  position: relative;
  overflow: hidden;
  min-height: 85vh;
  display: flex;
  align-items: center;
}

.services-section,
.projects-section,
.blog-section,
.why-choose-us {
  content-visibility: auto;
  contain-intrinsic-size: 1px 600px;
}

.hero-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    to bottom,
    rgba(15, 23, 42, 0.9) 0%,
    rgba(15, 23, 42, 0.75) 100%
  );
  z-index: 0;
}

.hero-content {
  position: relative;
  z-index: 1;
  max-width: 1000px;
  margin: 0 auto;
  padding: 2rem 0;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.main-title {
  color: #ffffff;
  margin-bottom: 1.5rem;
  font-size: 4.5rem;
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -1px;
}

.highlight {
  background: linear-gradient(90deg, #60a5fa 0%, #3b82f6 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  position: relative;
  display: inline-block;
}

.subtitle {
  color: #94a3b8;
  font-size: 1.5rem;
  line-height: 1.6;
  margin-bottom: 3.5rem;
  max-width: 800px;
  font-weight: 400;
}

.hero-stats {
  display: flex;
  justify-content: center;
  gap: 5rem;
  margin-bottom: 4rem;
  padding: 2.5rem 4rem;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.stat-number {
  font-size: 3rem;
  font-weight: 800;
  color: #ffffff;
  margin-bottom: 0.5rem;
  line-height: 1;
}

.stat-label {
  font-size: 1.1rem;
  color: #94a3b8;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.tech-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: center;
}

.tech-badge {
  background: rgba(59, 130, 246, 0.1);
  padding: 0.75rem 1.5rem;
  border-radius: 999px;
  font-size: 1rem;
  color: #60a5fa;
  border: 1px solid rgba(59, 130, 246, 0.2);
  transition: all 0.3s ease;
  font-weight: 500;
}

.tech-badge:hover {
  background: rgba(59, 130, 246, 0.2);
  transform: translateY(-2px);
  color: #ffffff;
}

.section-header {
  text-align: center;
  margin-bottom: 3rem;
}

.section-title {
  font-size: 2.5rem;
  color: var(--primary-color);
  margin-bottom: 1rem;
  font-weight: 700;
  position: relative;
  display: inline-block;
}

.section-title::after {
  content: '';
  position: absolute;
  bottom: -10px;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 4px;
  background: linear-gradient(90deg, var(--primary-color) 0%, var(--accent-color) 100%);
  border-radius: 2px;
}

.section-subtitle {
  color: var(--text-secondary);
  font-size: 1.2rem;
  margin-top: 2rem;
}

.cards-container {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
}

.service-card {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--background-white);
  border-radius: 16px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid rgba(var(--primary-rgb), 0.08);
  overflow: hidden;
  position: relative;
  isolation: isolate;
}

.service-card::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    rgba(var(--primary-rgb), 0.05),
    rgba(var(--accent-rgb), 0.05)
  );
  opacity: 0;
  transition: opacity 0.4s ease;
  z-index: -1;
}

.service-card:hover {
  transform: translateY(-8px);
  box-shadow:
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 8px 10px -6px rgba(0, 0, 0, 0.05);
  border-color: rgba(var(--accent-rgb), 0.15);
}

.service-card:hover::before {
  opacity: 1;
}

:deep(.p-card) {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: transparent;
  border: none;
  box-shadow: none !important;
}

:deep(.p-card-body) {
  padding: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
}

:deep(.p-card-content) {
  padding: 1.75rem !important;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: transparent;
  position: relative;
  overflow: visible;
  box-shadow: none !important;
}

.card-image {
  position: relative;
  width: 100%;
  height: 240px;
  overflow: hidden;
  border-radius: 12px 12px 0 0;
}

.card-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: transform;
}

.image-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.6));
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: all 0.4s ease;
}

.image-overlay i {
  font-size: 3.5rem;
  color: white;
  transform: translateY(20px) scale(0.95);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.2));
}

.service-card:hover .image-overlay {
  opacity: 1;
}

.service-card:hover .image-overlay i {
  transform: translateY(0) scale(1);
}

.service-card:hover .card-image img {
  transform: scale(1.15);
}

.service-title {
  padding: 1.75rem 1.75rem 1.25rem;
  border-bottom: 1px solid rgba(var(--primary-rgb), 0.08);
  background: linear-gradient(
    to right,
    rgba(var(--primary-rgb), 0.02),
    rgba(var(--accent-rgb), 0.02)
  );
}

.service-title h3 {
  font-size: 1.5rem;
  color: var(--primary-color);
  margin: 0;
  font-weight: 600;
  transition: color 0.3s ease;
  letter-spacing: -0.02em;
}

.service-card:hover .service-title h3 {
  color: var(--accent-color);
}

.service-description {
  color: var(--text-secondary);
  line-height: 1.7;
  margin-bottom: 1.75rem;
  font-size: 1rem;
  flex: 1;
}

.learn-more-btn {
  color: var(--accent-color);
  font-weight: 600;
  padding: 0;
  margin-top: auto;
  align-self: flex-start;
  position: relative;
  padding-right: 2rem;
  font-size: 0.95rem;
  overflow: visible;
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  transition: all 0.3s ease;
}

.learn-more-btn:hover {
  color: var(--primary-color);
}

.learn-more-btn i {
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 1rem;
}

.learn-more-btn:hover i {
  transform: translate(6px, -50%);
}

.project-card {
  background: var(--background-white);
  border-radius: 20px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid rgba(var(--primary-rgb), 0.05);
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  isolation: isolate;
}

.project-card::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    rgba(var(--primary-rgb), 0.04),
    rgba(var(--accent-rgb), 0.06)
  );
  opacity: 0;
  transition: opacity 0.5s ease;
  z-index: -1;
}

.project-card:hover {
  transform: translateY(-10px);
  box-shadow:
    0 25px 45px -10px rgba(var(--primary-rgb), 0.15),
    0 15px 25px -5px rgba(var(--accent-rgb), 0.1);
  border-color: rgba(var(--accent-rgb), 0.2);
}

.project-card:hover::before {
  opacity: 1;
}

.project-link {
  text-decoration: none;
  color: inherit;
  display: block;
  height: 100%;
}

.project-logo {
  position: relative;
  width: 100%;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  background: linear-gradient(135deg, rgba(var(--primary-rgb), 0.03) 0%, rgba(var(--accent-rgb), 0.08) 100%);
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.5s ease;
  border: 1px solid rgba(var(--primary-rgb), 0.03);
}

.project-logo img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  max-width: 150px;
  max-height: 150px;
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.05));
}

.project-info {
  padding: 1.5rem 0.5rem 0.5rem;
  text-align: center;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.project-title {
  font-size: 1.25rem;
  color: var(--primary-color);
  margin: 0 0 0.75rem 0;
  font-weight: 700;
  transition: color 0.3s ease;
}

.project-description {
  font-size: 0.95rem;
  color: var(--text-secondary);
  margin: 0;
  display: -webkit-box;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.6;
}

.project-card:hover .project-logo {
  background: linear-gradient(135deg, rgba(var(--primary-rgb), 0.05) 0%, rgba(var(--accent-rgb), 0.12) 100%);
  border-color: rgba(var(--accent-rgb), 0.1);
}

.project-card:hover .project-logo img {
  transform: scale(1.15) translateY(-5px);
  filter: drop-shadow(0 12px 16px rgba(0, 0, 0, 0.15));
}

.project-card:hover .project-title {
  color: var(--accent-color);
}

section {
  padding: 4rem 2rem;
  background-color: var(--background-light);
}

section:nth-child(even) {
  background-color: var(--background-white);
}

.projects-carousel,
.blog-carousel {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 1rem;
}

.carousel-item {
  padding: 0.5rem;
}

:deep(.p-carousel) {
  .p-carousel-content {
    padding: 0;
  }

  .p-carousel-item[aria-hidden="true"],
  .p-carousel-item[data-p-carousel-item-active="false"] {
    visibility: hidden;
    pointer-events: none;
  }

  .p-carousel-item[data-p-carousel-item-active="true"],
  .p-carousel-item:not([aria-hidden="true"]) {
    visibility: visible;
    pointer-events: auto;
  }

  .p-carousel-indicators {
    margin-top: 2rem;
  }

  .p-carousel-indicator button {
    width: 10px !important;
    height: 10px !important;
    border-radius: 50% !important;
    background-color: var(--border-color) !important;
    transition: all 0.3s ease;
  }

  .p-carousel-indicator.p-highlight button {
    background-color: var(--accent-color) !important;
    transform: scale(1.2);
  }
}

.blog-card {
  background: var(--background-white);
  border-radius: 16px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid rgba(var(--primary-rgb), 0.08);
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  isolation: isolate;
}

.blog-card::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    rgba(var(--primary-rgb), 0.05),
    rgba(var(--accent-rgb), 0.05)
  );
  opacity: 0;
  transition: opacity 0.4s ease;
  z-index: -1;
}

.blog-card:hover {
  transform: translateY(-8px);
  box-shadow:
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 8px 10px -6px rgba(0, 0, 0, 0.05);
  border-color: rgba(var(--accent-rgb), 0.15);
}

.blog-card:hover::before {
  opacity: 1;
}

.blog-image {
  position: relative;
  width: 100%;
  height: 240px;
  overflow: hidden;
}

.blog-image::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 50%;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.5), transparent);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.blog-card:hover .blog-image::after {
  opacity: 1;
}

.blog-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.blog-card:hover .blog-image img {
  transform: scale(1.1);
}

.blog-content {
  padding: 1.5rem;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.blog-meta {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  color: var(--text-secondary);
  font-size: 0.9rem;
}

.blog-date,
.blog-read-time {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.blog-date::before {
  content: '\e934';
  font-family: 'primeicons';
  font-size: 1rem;
}

.blog-read-time::before {
  content: '\e930';
  font-family: 'primeicons';
  font-size: 1rem;
}

.blog-title {
  font-size: 1.4rem;
  color: var(--primary-color);
  margin: 0 0 1rem 0;
  line-height: 1.4;
  font-weight: 600;
  transition: color 0.3s ease;
}

.blog-card:hover .blog-title {
  color: var(--accent-color);
}

.blog-summary {
  color: var(--text-secondary);
  margin: 0 0 1.5rem 0;
  line-height: 1.6;
  display: -webkit-box;
  line-clamp: 3;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex: 1;
}

.read-more {
  color: var(--accent-color);
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: auto;
  transition: all 0.3s ease;
}

.read-more i {
  transition: transform 0.3s ease;
  font-size: 1.1rem;
}

.blog-card:hover .read-more {
  color: var(--primary-color);
}

.blog-card:hover .read-more i {
  transform: translateX(5px);
}

.blog-link {
  text-decoration: none;
  color: inherit;
  display: block;
  height: 100%;
}

@media (max-width: 768px) {
  .hero-section {
    padding: 6rem 1rem;
    min-height: auto;
  }

  .hero-content {
    padding: 1.5rem;
  }

  .services-section,
  .projects-section,
  .blog-section {
    padding: 3rem 1rem;
  }

  .cards-container {
    grid-template-columns: 1fr;
    padding: 0;
  }

  .projects-carousel,
  .blog-carousel {
    padding: 0;
  }

  .main-title {
    font-size: 2.8rem;
  }

  .subtitle {
    font-size: 1.2rem;
  }

  .hero-stats {
    flex-direction: column;
    gap: 2rem;
    padding: 1.5rem;
  }

  .tech-badges {
    gap: 0.8rem;
  }

  .tech-badge {
    padding: 0.6rem 1.2rem;
    font-size: 1rem;
  }

  .section-title {
    font-size: 2rem;
  }
}
</style>
